// src/components/SimpleFormComponents/SimpleCpf.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Form, Modal } from 'antd';
import { useEffect, useMemo, useRef, useCallback } from 'react';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import CpfCnpjInput from '../JarvislyComponents/CpfCnpjInput';
import { returnOnlyNumbers } from 'utils/helpers';
import { isValidCpf } from 'utils/validation';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';
import { useSimpleFormContext } from './SimpleFormProvider';
import translateX from 'utils/translateX';
import getValueFromFlattenObj from 'utils/getValueFromFlattenObj';
import entitiesService from 'services/entitiesService';
import useZustand from 'hooks/useZustand';
import { useNavigate } from 'react-router-dom';
import appService from 'services/appService';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleCpf = ({
  name,
  subdocumentField,
  disabled,
  onBlur,
  searchCpfByApi = false, // If true, search external APIs for auto-fill (future implementation)
  unique = false, // If true, search for existing entity with same CPF
  ...props
}) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const {
    locked,
    getFormCpfState,
    setFormCpfState,
    setFormZipState, // needed to set ZIP state after CPF API fills address
    fieldsWithError,
    disabledFields,
    handleCpfChange,
    handleCpfBlur,
    data,
    form,
    setUniqueEntity,
    reloadEntirePage, // to determine how to handle unique entity loading
    formTmpState, // consume to trigger re-render when cpf state changes
  } = useSimpleFormContext();

  const navigate = useNavigate();

  // Get zustand module store for unique entity handling
  const storedModuleConfig = useZustand('module', 'config');
  const storedCurrentPage = useZustand('module', 'currentPage');

  const [modal, contextHolder] = Modal.useModal();

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------

  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;

  // read value from Form state so it reacts to typing, paste and programmatic setFieldsValue
  const currentValueRaw = Form.useWatch(fullFieldName, form);
  const currentValue =
    typeof currentValueRaw === 'object' && currentValueRaw !== null
      ? currentValueRaw.value
      : currentValueRaw;
  const currentCountryCode =
    typeof currentValueRaw === 'object' && currentValueRaw !== null
      ? currentValueRaw.countryCode
      : null;

  const dataValueRaw = getValueFromFlattenObj(data, flattenFieldName);
  const dataValue =
    typeof dataValueRaw === 'object' && dataValueRaw !== null ? dataValueRaw.value : dataValueRaw;
  const dataCountryCode =
    typeof dataValueRaw === 'object' && dataValueRaw !== null ? dataValueRaw.countryCode : null;
  // Read cpfState directly from formTmpState to ensure re-render when it changes
  const cpfState = formTmpState?.cpf?.[flattenFieldName] || null;
  const runOnce = useRef(false);

  // Ref to store the previous CPF value before unique search
  // Used to restore if user cancels the modal
  const previousCpfRef = useRef(null);

  const {
    customValidationStatus: baseStatus,
    customValidationMessage: baseMessage,
    handleChange,
  } = useCustomValidationStatus(
    {
      ...props,
      ...elementProps,
      // force 'validating' while API lookup is running
      loading: !!cpfState?.searching,
    },
    isValidCpf
  );

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------

  // Lock CPF field if entity is persisted AND was enriched via external API
  // This prevents changing the primary identifier of an enriched entity
  const isEnrichedAndPersisted = data?._id && data?.dataEnrichment?.isEnriched === true;
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName) || isEnrichedAndPersisted;

  // Calculate final status based on priorities
  const { finalStatus, finalMessage } = useMemo(() => {
    const currentCpf = returnOnlyNumbers(currentValue);
    const savedCpf = returnOnlyNumbers(dataValue);

    // Priority 1: Validating (searching CPF)
    if (cpfState?.searching) {
      return { finalStatus: 'validating', finalMessage: '' };
    }

    // Priority 2: Error from context (API failure or invalid CPF)
    const ctxMessageKey = fieldsWithError?.[flattenFieldName];
    if (ctxMessageKey) {
      return { finalStatus: 'error', finalMessage: translateX(ctxMessageKey) };
    }

    // Priority 3: Empty field - no validation display (required check happens on submit)
    if (!currentCpf) {
      return { finalStatus: '', finalMessage: '' };
    }

    // Priority 4: Invalid CPF format or API Failure
    if (currentCpf && !isValidCpf(currentCpf)) {
      return { finalStatus: 'error', finalMessage: baseMessage || '' };
    }

    // Priority 5: Success - current value matches saved value
    if ((savedCpf && currentCpf === savedCpf) || cpfState?.apiFailure) {
      return { finalStatus: 'success', finalMessage: '' };
    }

    // Priority 6: Valid but different from saved (or new entry)
    if (currentCpf && isValidCpf(currentCpf)) {
      return { finalStatus: 'success', finalMessage: '' };
    }

    // Fallback to base status
    return { finalStatus: baseStatus || '', finalMessage: baseMessage || '' };
  }, [
    currentValue,
    fieldsWithError,
    flattenFieldName,
    cpfState,
    baseStatus,
    baseMessage,
    dataValue,
  ]);

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const cpfValue = returnOnlyNumbers(currentValue);
    if (cpfValue && isValidCpf(cpfValue) && !currentCountryCode) {
      form.setFieldValue(fullFieldName, { value: cpfValue, countryCode: 'BR' });
    }
  }, [currentValue, currentCountryCode, form, fullFieldName]);

  useEffect(() => {
    if (runOnce.current) return;

    const currentCpf = returnOnlyNumbers(currentValue);
    const savedCpf = returnOnlyNumbers(dataValue);

    // Only set lastSearchFound if the current value matches the saved value
    // (i.e., form loaded with existing data from database)
    if (finalStatus === 'success' && savedCpf && currentCpf === savedCpf) {
      setFormCpfState(flattenFieldName, { lastSearchFound: currentCpf });
      runOnce.current = true;
    }
  }, [finalStatus, currentValue, dataValue, flattenFieldName, setFormCpfState]);

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  // Ref to track if we need to clear state on next blur (debounced state clearing)
  const needsClearStateRef = useRef(false);

  const handleCpfChangeWrapper = useCallback(
    ev => {
      const value = ev?.target?.value ?? ev?.currentTarget?.value ?? ev ?? '';

      // PERFORMANCE FIX: Only call handleChange for validation feedback
      // This is the minimum required for visual feedback during typing
      handleChange(ev);

      // PERFORMANCE FIX: Remove field from error state only (lightweight operation)
      // Don't update formCpfState on every keystroke - defer to blur
      handleCpfChange?.({
        ev,
        ...elementProps,
        value,
        subdocumentField,
      });

      // PERFORMANCE FIX: Mark that state needs to be cleared on blur
      // instead of clearing on every keystroke
      needsClearStateRef.current = true;
    },
    [handleChange, handleCpfChange, elementProps, subdocumentField]
  );

  /**
   * Search for existing entity by CPF (unique validation)
   * Shows modal if entity found, allowing user to load existing data
   *
   * Two modes based on reloadEntirePage prop:
   * 1. reloadEntirePage=true: Navigate to entity document URL and refresh
   * 2. reloadEntirePage=false: Merge entity data into form and zustand store
   */
  const handleUniqueSearch = useCallback(
    async (cpfValue, parentOnBlur) => {
      const cleanCpf = returnOnlyNumbers(cpfValue);

      // Skip if CPF is invalid
      if (!isValidCpf(cleanCpf)) {
        setUniqueEntity?.(null);
        parentOnBlur?.();
        return;
      }

      // Skip if CPF is the same as current data (editing existing document)
      const currentDataCpf = returnOnlyNumbers(data?.cpf?.value);
      if (cleanCpf === currentDataCpf) {
        parentOnBlur?.();
        return;
      }

      // Skip if this CPF was already searched (avoid re-fetching on every blur)
      if (cpfState?.lastSearchFound === cleanCpf) {
        parentOnBlur?.();
        return;
      }

      // Store the previous CPF value before searching
      // If this is a new document, previousCpf will be null/empty
      previousCpfRef.current = data?.cpf?.value || null;

      try {
        // Show loading message
        appService.message({
          flag: 'i',
          text: 'checking_document_duplicate',
          key: 'checkDocumentDuplicate',
          duration: 0,
        });

        // Search for existing entity with this CPF
        const foundEntity = await entitiesService.searchByDocument({
          key: 'cpf',
          value: cleanCpf,
        });

        // Destroy loading message
        appService.messageDestroy('checkDocumentDuplicate');

        if (foundEntity) {
          const entityName = foundEntity.name || foundEntity.fullName || 'Pessoa';

          // Show modal asking if the user wants to load an existing entity
          modal.confirm({
            title: translateX('document_already_exists'),
            content: translateX('document_already_exists_message', { name: entityName }),
            okText: translateX('yes_load_data'),
            cancelText: translateX('no_keep_current'),
            onOk: () => {
              if (reloadEntirePage) {
                // ---------------------------------------------------------------
                // CASE 1: reloadEntirePage=true
                // Navigate to entity document URL with the found entity's _id
                // ---------------------------------------------------------------
                const moduleUrl = storedModuleConfig?.url;
                const pageEndpoint = storedCurrentPage?.routing?.endpoint || '';

                if (moduleUrl && foundEntity._id) {
                  // Replace :_id in endpoint with the found entity's _id
                  const newEndpoint = pageEndpoint.replace(':_id', foundEntity._id);
                  const newUrl = `${moduleUrl}${newEndpoint}`;

                  // Navigate to the new URL (this will trigger a new GET for the entity)
                  navigate(newUrl, { replace: true });

                  // Clear unique entity since we're navigating to a new document
                  setUniqueEntity?.(null);
                }
              } else {
                // ---------------------------------------------------------------
                // CASE 2: reloadEntirePage=false
                // Merge entity data into form and zustand store
                // The entity's _id becomes entityId (not the document's _id)
                // ---------------------------------------------------------------

                // Get current form values to preserve specific fields
                const currentFormValues = form.getFieldsValue(true);

                // Prepare entity data for form (exclude _id and _metadata)
                const { _id: entityId, _metadata, ...entityFields } = foundEntity;

                // Build merged data: entity fields + entityId reference
                // Preserve avatar, backColor, foreColor from current form
                const mergedFormData = {
                  ...entityFields,
                  entityId, // Reference to the entity document
                  // Preserve these fields from current subscription
                  avatar: currentFormValues?.avatar,
                  backColor: currentFormValues?.backColor,
                  foreColor: currentFormValues?.foreColor,
                };

                // Update form with merged data
                form.setFieldsValue(mergedFormData);

                // FIX: Set ZIP state to prevent unnecessary CEP and GPS searches
                // when user interacts with address fields after loading existing entity
                if (entityFields?.address && setFormZipState) {
                  const addressData = entityFields.address;
                  const zipFieldName = 'address.zip';
                  const gpsHash =
                    addressData.zip && addressData.address1
                      ? `${addressData.zip}|${addressData.address1}|${addressData.number || ''}`
                      : null;

                  setFormZipState(zipFieldName, {
                    searching: false,
                    apiFailure: false,
                    lastSearchFound: addressData.zip,
                    data: addressData,
                    ...(gpsHash &&
                      addressData.addressCoordinates?.length && { lastGpsSearched: gpsHash }),
                  });
                }

                // NOTE: We intentionally do NOT update the zustand store here!
                // Updating the store triggers a re-fetch of the document from the backend,
                // which overwrites the form with the OLD data (causing the revert issue).
                // The form.setFieldsValue is sufficient to update the form values.
                // The uniqueEntity state will be used in handleFinish to get entityId.

                // Save unique entity reference for use in handleFinish
                // This allows handleFinish to restore the entity's metadata if needed
                setUniqueEntity?.({
                  _id: entityId,
                  documentKey: 'cpf',
                  documentValue: cleanCpf,
                  data: foundEntity,
                });
              }

              // NOTE: Do NOT call parentOnBlur() here!
              // parentOnBlur is the handleExternalApiSearch callback which:
              // 1. Sets entityId: null (for NEW entities from external API)
              // 2. Clears uniqueEntity state
              // This would overwrite the entityId and uniqueEntity we just set above.
              // parentOnBlur should only be called when entity is NOT found in database.
            },
            onCancel: () => {
              // User chose NOT to load existing entity
              // Restore the previous CPF value (or empty if new document)
              const previousValue = previousCpfRef.current;

              // Restore the CPF field to its previous value
              form.setFieldsValue({
                cpf: previousValue ? { value: previousValue } : { value: '' },
              });

              // Clear unique entity state
              setUniqueEntity?.(null);

              // Clear any CPF search flags
              setFormCpfState?.(flattenFieldName, {
                searching: false,
                found: false,
                data: null,
                lastSearchFound: null,
              });

              // Focus back on the CPF field
              setTimeout(() => {
                elementRef?.current?.focus?.();
              }, 100);

              // Do NOT call parent onBlur if user cancels
            },
          });
        } else {
          // No existing entity found - clear unique entity state
          setUniqueEntity?.(null);

          // Mark this CPF as searched to prevent re-fetching on every blur
          setFormCpfState?.(flattenFieldName, {
            lastSearchFound: cleanCpf,
          });

          parentOnBlur?.();
        }
      } catch (error) {
        console.error('[SimpleCpf:handleUniqueSearch] Error:', error);
        appService.messageDestroy('checkDocumentDuplicate');
        setUniqueEntity?.(null);

        // Mark this CPF as searched even on error to prevent re-fetching
        setFormCpfState?.(flattenFieldName, {
          lastSearchFound: cleanCpf,
        });

        parentOnBlur?.();
      }
    },
    [
      data?.cpf?.value,
      form,
      modal,
      setUniqueEntity,
      reloadEntirePage,
      storedModuleConfig,
      storedCurrentPage,
      navigate,
      setFormCpfState,
      setFormZipState,
      flattenFieldName,
      elementRef,
      cpfState?.lastSearchFound,
    ]
  );

  /**
   * Search CPF via external APIs (future implementation)
   * and auto-fill form with person data
   *
   * NOTE: This is a placeholder for future API integration.
   * Currently, there is no public API for CPF lookup in Brazil.
   * When an API becomes available, implement similar to handleExternalApiSearch in SimpleCnpj.jsx
   */
  const handleExternalApiSearch = useCallback(
    async cpfValue => {
      const cleanCpf = returnOnlyNumbers(cpfValue);

      // Skip if CPF is invalid
      if (!isValidCpf(cleanCpf)) {
        return;
      }

      // Skip if CPF is the same as current data (editing existing document)
      const currentDataCpf = returnOnlyNumbers(data?.cpf?.value);
      if (cleanCpf === currentDataCpf) {
        return;
      }

      // Skip if this CPF was already searched externally (avoid re-fetching on every blur)
      if (cpfState?.lastSearchFound === cleanCpf) {
        return;
      }

      // -----------------------------------------------------------------------
      // FUTURE IMPLEMENTATION:
      // When a CPF API becomes available, implement here similar to SimpleCnpj.jsx
      // -----------------------------------------------------------------------
      //
      // try {
      //   setFormCpfState?.(flattenFieldName, { searching: true });
      //   appService.message({
      //     flag: 'i',
      //     text: 'searching_cpf_external',
      //     key: 'searchCpfExternal',
      //     duration: 0,
      //   });
      //
      //   const result = await searchCpfExternal(cleanCpf);
      //   appService.messageDestroy('searchCpfExternal');
      //
      //   if (result?.data?.success) {
      //     // Transform and set form values
      //     // ...
      //   }
      // } catch (error) {
      //   console.error('[SimpleCpf:handleExternalApiSearch] Error:', error);
      // }
      // -----------------------------------------------------------------------

      // For now, just mark search as complete (no API available)
      setFormCpfState?.(flattenFieldName, {
        searching: false,
        lastSearchFound: cleanCpf,
        apiFailure: false,
      });

      // Clear unique entity since we're creating a new entity
      setUniqueEntity?.(null);

      // Set entityType to 'person' and entityId to null for new entities
      const currentFormValues = form.getFieldsValue(true);
      form.setFieldsValue({
        entityType: 'person',
        dataOrigin: currentFormValues?.dataOrigin || 'new_data',
        entityId: null,
        // NOTE: When CPF API becomes available, add dataEnrichment here:
        // dataEnrichment: {
        //   isEnriched: true,
        //   enrichedAt: new Date().toISOString(),
        //   provider: result.resolvedName || 'external',
        //   status: 'found',
        // },
      });
    },
    [
      data?.cpf?.value,
      form,
      flattenFieldName,
      setFormCpfState,
      setUniqueEntity,
      cpfState?.lastSearchFound,
    ]
  );

  const handleCpfBlurWrapper = async ev => {
    const value = ev?.target?.value ?? ev?.currentTarget?.value ?? ev ?? '';
    const cleanCpf = returnOnlyNumbers(value);

    // PERFORMANCE FIX: Clear deferred state changes from onChange
    // This runs once on blur instead of on every keystroke
    if (needsClearStateRef.current) {
      needsClearStateRef.current = false;

      // Clear unique entity when CPF changes
      if (unique) {
        setUniqueEntity?.(null);
      }

      // Clear lastSearchFound when CPF changes to allow new search
      setFormCpfState?.(flattenFieldName, {
        lastSearchFound: null,
        apiFailure: false,
      });

      // Destroy any existing notification
      appService.notificationDestroy('cpfApiErrorNotificationKey');
    }

    // Skip if CPF is invalid
    if (!isValidCpf(cleanCpf)) {
      return;
    }

    // Create a function for calling handleCpfBlur from context
    const callContextBlur = () => {
      handleCpfBlur?.({
        cpf: elementProps,
        ev,
        value,
        subdocumentField,
      });
    };

    // Flow:
    // 1. If unique=true: search internal database first (replaces handleCpfBlur/getByField)
    //    - If found: show modal (handled by handleUniqueSearch)
    //    - If NOT found AND searchCpfByApi=true: search external APIs
    // 2. If unique=false AND searchCpfByApi=true: search external APIs directly
    // NOTE: When unique=true, we don't call handleCpfBlur (callContextBlur) because
    // handleUniqueSearch already handles the entity lookup, avoiding duplicate getByField calls

    if (unique) {
      // Search internal database first via handleUniqueSearch
      // This replaces the getByField call in handleCpfBlur when unique=true
      await handleUniqueSearch(value, async () => {
        // This callback is called when no entity is found internally
        // If searchCpfByApi is enabled, search external APIs
        if (searchCpfByApi) {
          await handleExternalApiSearch(value);
        }
        // NOTE: We intentionally DO NOT call callContextBlur() here
        // because handleUniqueSearch already handles entity search
      });
    } else if (searchCpfByApi) {
      // No unique validation, but search external APIs
      await handleExternalApiSearch(value);
      callContextBlur();
    }
    // else: No unique validation, no external API search
    // Just validate format (already done above) - don't call any API
  };

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  // Show tooltip when field is disabled because entity was enriched via API
  const enrichedTooltip = isEnrichedAndPersisted ? translateX('entity_enriched_via_api') : props.tooltip;

  return (
    <>
      {contextHolder}
      <SimpleFormItem
        {...props}
        name={fullFieldName}
        elementProps={elementProps}
        customValidationStatus={finalStatus}
        customValidationMessage={finalMessage}
        tooltip={enrichedTooltip}
        // IMPORTANT: keep Form.Item feedback off to avoid AntD injecting suffix dynamically
        showFeedback={false}
      >
        <CpfCnpjInput
          ref={elementRef}
          id={flattenFieldName}
          onBlur={handleCpfBlurWrapper}
          onChange={handleCpfChangeWrapper}
          disabled={isDisabled}
          status={finalStatus}
          loading={cpfState?.searching || false}
          profile="cpf"
          // provide status & loading for static suffix rendering inside input
        />
      </SimpleFormItem>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleCpf;

// *******************************************************************************
// HELPERS
// *******************************************************************************
